import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeBounce } from 'better-react-spinkit';
import DOMPurify from 'dompurify';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import Linkify from 'linkify-html';
import moment from 'moment';
import { setChatAlertActiveId } from 'actions';
import { trackQuickReplySelected } from 'analytics/quickReplyEventAnalytics';
import { sendQuickReplyMessage } from 'api/messages';
import * as colors from 'components/common/colorConstants';
import { handleError } from 'services/ErrorService/ErrorService';
import { setAnchorElementsTarget } from '../../../util/util';
import {
  PopupWrapper,
  BouncingDotsContainer,
  Messages,
  Header,
  HeadingBold,
  Timestamp,
  MessageWrapper,
  MessageText,
  Actions,
  CtaText,
  CtaButtonWrapper,
  QuickReplyButtonWrapper,
  QuickReplyButton,
  PurpleCircle,
  Dots,
  DismissButtonWrapper,
  Button,
} from './ContextualPopover.styles';
import {
  hasPartnerRespondedToReachOut,
  isValidPartnerReachOutTicket,
  partnerReachOutRetryNeeded,
} from 'util/ticketSelector';

const propTypes = {
  isLoading: PropTypes.bool,
  labelButtonsWithActions: PropTypes.arrayOf(PropTypes.array).isRequired,
  ticket: PropTypes.shape({
    ticketId: PropTypes.string.isRequired,
  }),
  ticketAssigned: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  isQuickRepliesEnabled: PropTypes.bool,
  quickReplies: PropTypes.array,
  analyticsPayload: PropTypes.object,
  closePopover: PropTypes.func,
  onClose: PropTypes.func,
};

export const ContextualPopover = ({
  isLoading = false,
  labelButtonsWithActions,
  ticket = {},
  ticketAssigned,
  messages,
  isQuickRepliesEnabled = false,
  quickReplies = [],
  trackQuickRepliesDisplayed = noop,
  closePopover = noop,
  onClose = noop,
}) => {
  const dispatch = useDispatch();

  const expertFirstName = useSelector((state) => get(state.user, 'firstName', 'Expert'));

  const [messageLoading, setMessageLoading] = useState('');

  useEffect(() => {
    return () => {
      onClose();
    };
  }, [onClose]);

  const hasQuickReplies = isQuickRepliesEnabled && !isEmpty(quickReplies);

  useEffect(() => {
    if (hasQuickReplies) {
      trackQuickRepliesDisplayed();
    }
  }, [hasQuickReplies, ticket, trackQuickRepliesDisplayed]);

  const handleQuickReplySend = async (quickReply) => {
    try {
      setMessageLoading(quickReply.text);
      await sendQuickReplyMessage(ticket, quickReply.text);
      trackQuickReplySelected(ticket, quickReply);
    } catch (error) {
      handleError({ error, customMessage: "Something went wrong. Quick reply hasn't been sent.", showPopup: true });
    } finally {
      setMessageLoading('');
      dispatch(setChatAlertActiveId(null));
    }
  };

  const altStylesActive = (label) => {
    return ['Reject', 'Dismiss'].includes(label);
  };

  const getButtonSectionHeader = () => {
    if (hasPartnerRespondedToReachOut(ticket)) {
      return 'Partner has responded to the request.';
    }
    if (partnerReachOutRetryNeeded(ticket)) {
      return 'There was a problem. Please resubmit the request.';
    }
    if (ticketAssigned) {
      return hasQuickReplies ? 'Please choose a quick reply to extend the chat.' : 'Please respond to the customer.';
    }

    return 'Please take or reject the chat';
  };

  const conversationUpdatedAt = moment(ticket.conversationUpdatedAt).format('h:mm a');

  return (
    <PopupWrapper role="dialog">
      {isLoading ? (
        <BouncingDotsContainer data-testid={'spinner'}>
          <ThreeBounce color={colors.whiteText} size={6} />
        </BouncingDotsContainer>
      ) : (
        <>
          <Messages>
            <Header>
              <HeadingBold>
                <span>{ticket.simplrTicketNumber}</span> <span>{ticket.customerFirstName || 'Chat'}</span>
              </HeadingBold>
              <Timestamp>{conversationUpdatedAt}</Timestamp>
            </Header>

            {!isValidPartnerReachOutTicket(ticket) && renderMessageContent()}
          </Messages>

          <Actions>
            <CtaText>
              <span>Customer is waiting. </span>
              {getButtonSectionHeader()}
            </CtaText>

            <CtaButtonWrapper>{renderCtaButtons()}</CtaButtonWrapper>
          </Actions>
        </>
      )}
    </PopupWrapper>
  );

  function renderMessageContent() {
    if (isEmpty(messages)) {
      return null;
    }

    const [lastCustomerMessage, lastExpertMessage] = messages;

    return (
      <>
        <MessageWrapper>
          <MessageText
            dangerouslySetInnerHTML={{
              __html: setAnchorElementsTarget(Linkify(DOMPurify.sanitize(lastCustomerMessage))),
            }}
          />
        </MessageWrapper>
        {lastExpertMessage && (
          <MessageWrapper>
            <HeadingBold>{`${expertFirstName}'s previous response`}</HeadingBold>
            <MessageText
              dangerouslySetInnerHTML={{
                __html: setAnchorElementsTarget(Linkify(DOMPurify.sanitize(lastExpertMessage))),
              }}
            />
          </MessageWrapper>
        )}
      </>
    );
  }

  function renderCtaButtons() {
    return hasQuickReplies ? (
      <>
        <QuickReplyButtonWrapper>
          {quickReplies.map((quickReply) => (
            <QuickReplyButton
              key={quickReply.text}
              type="button"
              loading={messageLoading}
              selected={messageLoading === quickReply.text}
              onClick={() => handleQuickReplySend(quickReply)}
            >
              {messageLoading === quickReply.text ? renderButtonLoader() : quickReply.text}
            </QuickReplyButton>
          ))}
        </QuickReplyButtonWrapper>
        <DismissButtonWrapper>
          <Button
            type="button"
            altStylesActive={true}
            onClick={(e) => {
              e.stopPropagation();
              closePopover();
            }}
          >
            Dismiss
          </Button>
        </DismissButtonWrapper>
      </>
    ) : (
      labelButtonsWithActions.map(([label, action]) => (
        <Button
          key={label}
          altStylesActive={altStylesActive(label)}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            action(ticket.ticketId);
            dispatch(setChatAlertActiveId(null));
          }}
        >
          {label}
        </Button>
      ))
    );
  }

  function renderButtonLoader() {
    return (
      <PurpleCircle>
        <Dots size={1} color={colors.whiteText} />
      </PurpleCircle>
    );
  }
};

export default ContextualPopover;

ContextualPopover.propTypes = propTypes;

const arrowWidthPx = 10;
const arrowOffsetPx = 42;

export const arrowTipLeftOffset = arrowOffsetPx + arrowWidthPx / 2;
export const arrowTipTopOffsetPx = 6;
